import React, { useEffect, useRef, useState } from 'react'
import Plotly from 'plotly.js'
import debounce from 'debounce'
import styled from 'styled-components'

Plotly.register(require('plotly.js/lib/locales/pt-br.js'))

const PLOT_FULLSCREEN_BUTTON_MIN_HEIGHT = 160

const plotFullscreenIcon: Plotly.Icon = {
  width: 24,
  height: 24,
  path: 'M4 10V4h6V2H2v8h2zm10-8v2h6v6h2V2h-8zm6 18h-6v2h8v-8h-2zm-16 0v-6H2v8h8v-2z',
}

const STANDARD_MODEBAR_BUTTONS = ['zoom2d', 'pan2d', 'resetScale2d', 'toImage'] as const

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;

  .modebar-container {
    width: 100%;
    pointer-events: none;
  }

  .modebar {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    max-width: calc(100% - 8px);
    padding: 0;
    border-radius: 10px;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    transition:
      background-color 0.18s ease,
      box-shadow 0.18s ease,
      backdrop-filter 0.18s ease,
      padding 0.18s ease;
    pointer-events: auto;
  }

  .js-plotly-plot:hover .modebar--hover,
  .modebar--hover:hover {
    padding: 2px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
    backdrop-filter: blur(8px);
  }

  .modebar-group {
    display: inline-flex;
    align-items: center;
    margin-left: 0;
    gap: 2px;
    padding: 0;
    background: transparent;
    pointer-events: auto;
  }

  .modebar-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 8px;
    color: #334155;
    opacity: 1;
    pointer-events: auto;
  }

  .modebar-btn:hover {
    background: rgba(148, 163, 184, 0.18);
  }

  .modebar-btn svg {
    width: 16px;
    height: 16px;
  }

  .modebar-btn svg path {
    fill: currentColor;
  }
`

export interface NewPlot {
  data: Plotly.Data[]
  layout: Partial<Plotly.Layout>
  layoutPatch?: Partial<Plotly.Layout>
  config?: Partial<Plotly.Config>
  containerHeight?: string | number
}

export type PlotToolbarPreset = 'standard' | 'full' | 'none'

export interface PlotProps extends NewPlot {
  enableFullscreen?: boolean
  toolbarPreset?: PlotToolbarPreset
}

function shouldEnableFullscreenForHeight(height: Partial<Plotly.Layout>['height'] | string) {
  if (typeof height === 'number') return height >= PLOT_FULLSCREEN_BUTTON_MIN_HEIGHT
  if (typeof height === 'string') {
    const numericHeight = Number.parseFloat(height)
    return Number.isNaN(numericHeight) || numericHeight >= PLOT_FULLSCREEN_BUTTON_MIN_HEIGHT
  }

  return true
}

function resolveContainerHeight(
  height: Partial<Plotly.Layout>['height'] | string,
  isFullscreen: boolean,
) {
  if (isFullscreen) return '100dvh'
  if (typeof height === 'number') return `${height}px`
  return height ?? 'auto'
}

function mergeModeBarButtonsToAdd(
  currentButtons: Plotly.ModeBarButtonAny[] | undefined,
  nextButtons: Plotly.ModeBarButtonAny[] | undefined,
) {
  const buttons = [...(currentButtons ?? []), ...(nextButtons ?? [])]

  if (!buttons.length) {
    return undefined
  }

  const seen = new Set<string>()

  return buttons.filter(button => {
    const iconPath =
      typeof button === 'string'
        ? undefined
        : typeof button.icon === 'string'
          ? button.icon
          : button.icon?.path
    const key =
      typeof button === 'string'
        ? button
        : [button.name, button.title, iconPath].filter(Boolean).join(':')

    if (!key) {
      return true
    }
    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

function buildStandardModeBarButtons(modeBarButtonsToAdd: Plotly.ModeBarButtonAny[] | undefined) {
  const baseButtons = [...STANDARD_MODEBAR_BUTTONS]

  if (!modeBarButtonsToAdd?.length) {
    return [baseButtons] as Plotly.ModeBarButtonAny[][]
  }

  return [baseButtons, modeBarButtonsToAdd] as Plotly.ModeBarButtonAny[][]
}

function buildConfig(
  config: Partial<Plotly.Config> | undefined,
  toolbarPreset: PlotToolbarPreset,
  modeBarButtonsToAdd: Plotly.ModeBarButtonAny[] | undefined,
) {
  const baseConfig: Partial<Plotly.Config> = {
    locale: 'pt-BR',
    displaylogo: false,
    displayModeBar: toolbarPreset === 'none' ? false : 'hover',
    scrollZoom: false,
    doubleClick: 'reset+autosize',
    responsive: true,
    showTips: false,
  }

  const nextConfig: Partial<Plotly.Config> = {
    ...baseConfig,
    ...config,
  }

  if (toolbarPreset !== 'none') {
    if (toolbarPreset === 'standard') {
      const mergedButtonsToAdd = mergeModeBarButtonsToAdd(
        config?.modeBarButtonsToAdd,
        modeBarButtonsToAdd,
      )

      nextConfig.modeBarButtons = config?.modeBarButtons ?? buildStandardModeBarButtons(mergedButtonsToAdd)
      delete nextConfig.modeBarButtonsToAdd
      delete nextConfig.modeBarButtonsToRemove
    } else {
      const mergedButtonsToAdd = mergeModeBarButtonsToAdd(
        config?.modeBarButtonsToAdd,
        modeBarButtonsToAdd,
      )

      if (mergedButtonsToAdd?.length) {
        nextConfig.modeBarButtonsToAdd = mergedButtonsToAdd
      }
    }
  }

  return nextConfig
}

export default function Plot({
  data,
  layout,
  layoutPatch,
  config,
  containerHeight,
  enableFullscreen,
  toolbarPreset = 'standard',
}: PlotProps) {
  const frameRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const isMountedRef = useRef(true)
  const renderVersionRef = useRef(0)
  const plotTaskRef = useRef<Promise<unknown>>(Promise.resolve())
  const [isFullscreen, setIsFullscreen] = useState(false)

  const shouldEnableFullscreen =
    enableFullscreen ?? shouldEnableFullscreenForHeight(containerHeight ?? layout.height)

  const runPlotTask = (task: () => Promise<unknown> | unknown) => {
    plotTaskRef.current = plotTaskRef.current
      .catch(() => undefined)
      .then(() => {
        if (!isMountedRef.current) return
        return task()
      })

    return plotTaskRef.current
  }

  const performResizePlot = async (plotElement: HTMLDivElement | null, renderVersion?: number) => {
    if (
      !plotElement ||
      !plotElement.classList.contains('js-plotly-plot') ||
      ref.current !== plotElement ||
      (typeof renderVersion === 'number' && renderVersionRef.current !== renderVersion)
    ) {
      return
    }

    try {
      await Plotly.relayout(plotElement, {
        width: plotElement.clientWidth,
        height: plotElement.clientHeight,
      })
    } catch (error) {
      if (!isMountedRef.current || ref.current !== plotElement) return
      console.warn('[Plot] Não foi possível redimensionar gráfico', error)
    }
  }

  const queueResizePlot = (plotElement: HTMLDivElement | null, renderVersion?: number) =>
    runPlotTask(() => performResizePlot(plotElement, renderVersion))

  const queueRelayoutPlot = (
    plotElement: HTMLDivElement | null,
    nextLayoutPatch: Partial<Plotly.Layout> | undefined,
    renderVersion?: number,
  ) =>
    runPlotTask(async () => {
      if (
        !plotElement ||
        !nextLayoutPatch ||
        !Object.keys(nextLayoutPatch).length ||
        !plotElement.classList.contains('js-plotly-plot') ||
        ref.current !== plotElement ||
        (typeof renderVersion === 'number' && renderVersionRef.current !== renderVersion)
      ) {
        return
      }

      try {
        await Plotly.relayout(plotElement, nextLayoutPatch)
      } catch (error) {
        if (!isMountedRef.current || ref.current !== plotElement) return
        console.warn('[Plot] Não foi possível aplicar ajuste de layout', error)
      }
    })

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false

      const plotElement = ref.current
      if (!plotElement) return

      void plotTaskRef.current
        .catch(() => undefined)
        .finally(() => {
          if (plotElement.classList.contains('js-plotly-plot')) {
            Plotly.purge(plotElement)
          }
        })
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const frameElement = frameRef.current
    if (!frameElement) return

    const syncFullscreenState = () => {
      const plotElement = ref.current
      setIsFullscreen(document.fullscreenElement === frameElement)

      if (!plotElement || !plotElement.classList.contains('js-plotly-plot')) return

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          void queueResizePlot(plotElement)
        })
      })
    }

    syncFullscreenState()
    document.addEventListener('fullscreenchange', syncFullscreenState)

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState)
    }
  }, [])

  useEffect(() => {
    const frameElement = frameRef.current
    const plotElement = ref.current
    if (plotElement && frameElement) {
      const renderVersion = ++renderVersionRef.current
      const modeBarButtonsToAdd =
        shouldEnableFullscreen && typeof frameElement.requestFullscreen === 'function'
          ? [
              {
                name: 'toggleFullscreen',
                title: 'Alternar tela cheia',
                icon: plotFullscreenIcon,
                click: () => {
                  void (async () => {
                    if (typeof document === 'undefined') return

                    try {
                      if (document.fullscreenElement === frameElement) {
                        await document.exitFullscreen()
                        return
                      }

                      if (document.fullscreenElement) {
                        await document.exitFullscreen()
                      }

                      await frameElement.requestFullscreen()
                    } catch (error) {
                      console.warn('[Plot] Não foi possível alternar tela cheia', error)
                    }
                  })()
                },
              },
            ]
          : undefined

      const nextConfig = buildConfig(config, toolbarPreset, modeBarButtonsToAdd)

      void runPlotTask(async () => {
        if (
          !isMountedRef.current ||
          ref.current !== plotElement ||
          renderVersionRef.current !== renderVersion
        ) {
          return
        }

        const renderPlot = plotElement.classList.contains('js-plotly-plot')
          ? Plotly.react
          : Plotly.newPlot

        try {
          await renderPlot(plotElement, data, layout, nextConfig)
        } catch (error) {
          if (
            !isMountedRef.current ||
            ref.current !== plotElement ||
            renderVersionRef.current !== renderVersion
          ) {
            return
          }

          console.warn('[Plot] Não foi possível renderizar gráfico', error)
          return
        }

        if (
          !isMountedRef.current ||
          ref.current !== plotElement ||
          renderVersionRef.current !== renderVersion
        ) {
          return
        }

        await performResizePlot(plotElement, renderVersion)
      })

      const relayout = debounce(() => {
        void queueResizePlot(plotElement, renderVersion)
      }, 1000)
      const resizeObserver = new ResizeObserver(relayout)

      resizeObserver.observe(frameElement)

      return () => {
        relayout.clear()
        resizeObserver.disconnect()
      }
    }
  }, [config, data, layout, shouldEnableFullscreen, toolbarPreset])

  useEffect(() => {
    const plotElement = ref.current
    const renderVersion = renderVersionRef.current

    if (!plotElement) {
      return
    }

    void queueRelayoutPlot(plotElement, layoutPatch, renderVersion)
  }, [layoutPatch])

  return (
    <Frame
      ref={frameRef}
      style={{
        height: resolveContainerHeight(containerHeight ?? layout.height, isFullscreen),
        minHeight: isFullscreen ? '100dvh' : undefined,
        padding: isFullscreen ? '16px' : 0,
        background: isFullscreen ? '#ffffff' : 'transparent',
      }}
    >
      <div
        ref={ref}
        style={{
          position: 'absolute',
          top: isFullscreen ? 16 : 0,
          left: isFullscreen ? 16 : 0,
          right: isFullscreen ? 16 : 0,
          bottom: isFullscreen ? 16 : 0,
        }}
      />
    </Frame>
  )
}
